import React, { useMemo, useState, useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from "../../../components/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import { FaTrash, FaEye } from "react-icons/fa";
import { ActionButton } from "../../../components/Forms/ActionButton";
import {
    useGetAdminNotifications,
    useGetNotificationTypes,
    useUpdateNotifications,
    useDeleteNotification,
    useMarkNotificationAsRead
} from "../../../queries/notifications";
import ActiveStatus from "../../../components/Common/ActiveStatus";

const NotificationsList = () => {
    const { data: notificationDataList, isPending: isNotificationsPending } = useGetAdminNotifications();
    const { mutateAsync: updateNotification, isPending: isUpdatingNotification } = useUpdateNotifications();
    const { data: notificationTypes } = useGetNotificationTypes();
    const markAsReadMutation = useMarkNotificationAsRead();

    const columnHelper = createColumnHelper();

    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        type: "",
        title: "",
    });

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleMarkAsRead = async (id) => {
        // optimistic UI
        setFilteredData(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        try {
            await markAsReadMutation.mutateAsync(id);
            toast.success("Marked as read");
        } catch (e) {
            // rollback on error
            setFilteredData(prev => prev.map(n => n._id === id ? { ...n, isRead: false } : n));
            toast.error("Failed to mark as read");
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = notificationDataList;

        if (filters.fromDate) {
            filtered = filtered.filter(
                (item) => new Date(item.createdAt) >= new Date(filters.fromDate)
            );
        }
        if (filters.toDate) {
            filtered = filtered.filter(
                (item) => new Date(item.createdAt) <= new Date(filters.toDate)
            );
        }
        if (filters.type) {
            filtered = filtered.filter(
                (item) => item.type?.toLowerCase() === filters.type.toLowerCase()
            );
        }
        if (filters.title) {
            filtered = filtered.filter(
                (item) =>
                    item.message?.toLowerCase().includes(filters.title.toLowerCase())
            );
        }
        setFilteredData(filtered);
    };



    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "srno",
                header: "Sr No.",
                cell: (info) => info.row.index + 1,
            }),
            columnHelper.accessor("title", {
                header: "Title",
                cell: (info) => info.getValue() || "N/A",
            }),
            columnHelper.accessor("message", {
                header: "Message",
                cell: (info) => (
                    <div title={info.getValue()} className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                        {info.getValue() || "N/A"}
                    </div>
                ),
            }),
            columnHelper.accessor("createdAt", {
                header: "Created At",
                align: "center",
                cell: (info) => (
                    <div className="text-center">
                        {new Date(info.getValue()).toLocaleDateString("en-IN", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                        })}
                        <br />
                        <small className="text-secondary">
                            {new Date(info.getValue()).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </small>
                    </div>
                ),
            }),
            columnHelper.display({
                id: "actions",
                header: "Actions",
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <div className="flex gap-2">
                            {!row.isRead ? (
                                <button
                                    onClick={() => handleMarkAsRead(row._id)}
                                    className="px-3 py-1 rounded bg-primary hover:bg-blue-200 text-blue-700"
                                >
                                    Mark as Read
                                </button>
                            ) : (
                                <span className="px-3 py-1 rounded bg-secondary  text-white font-semibold">
                                    Seen
                                </span>
                            )}
                        </div>
                    );
                },
            }),
        ],
    );

    useEffect(() => {
        if (notificationDataList) {
            setFilteredData(notificationDataList);
        }
    }, [notificationDataList]);

    if (isNotificationsPending) {
        return <p>Loading notifications...</p>;
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-md"
                onSubmit={handleSearch}
            >
                <div className="flex flex-wrap gap-4 p-5">
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">From Date</label>

                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="date"
                                name="fromDate"
                                value={filters.fromDate}
                                onChange={handleFilterChange}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base undefined"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">To Date</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="date"
                                name="toDate"
                                value={filters.toDate}
                                onChange={handleFilterChange}
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base undefined"
                            />
                        </div></div>
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Type</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                className="flex-1 select2 shrink self-stretch my-auto basis-0 px-4 py-2.5 outline-primary rounded-md capitalize text-base"
                            >
                                <option value="">All Types</option>
                                {notificationTypes?.map((t) => (
                                    <option key={t._id} value={t.types}>
                                        {t.types}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Title</label>
                        <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                            <input
                                type="text"
                                name="title"
                                value={filters.title}
                                onChange={handleFilterChange}
                                placeholder="Enter title"
                                className="flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base undefined"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 p-5">
                    <ActionButton
                        variant={"reset"}
                        text={"Reset"}
                        handleSubmit={() => {
                            setFilters({ fromDate: "", toDate: "", type: "", title: "" });
                            setFilteredData(notificationDataList);
                        }}
                    />
                    <ActionButton variant={"submit"} text={"Search"} />
                </div>
            </form>

            {/* Table */}
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-md">
                <div className="px-6">
                    <TanStackTable
                        data={filteredData}
                        columns={columns}
                        enablePagination={true}
                    />
                </div>
            </section>

            <ToastContainer position="top-right" autoClose={3000} />
        </main>
    );
};

export default NotificationsList;
